import { createContext, useContext, useRef } from 'react';

/**
 * 自研页签 keep-alive 的「激活」信号 Context。
 *
 * 背景：TabLayout 于 2026-07-02 用自研 keep-alive 替换了 keepalive-for-react 运行时，
 * 缓存页仅靠 display:none/block 切换显隐、组件不重挂。keepalive-for-react 的
 * useEffectOnActive 依赖其 <KeepAlive> Provider 提供 active 信号，而该 Provider 已不存在，
 * 默认 context 恒为 { active:false } → 列表页「返回刷新」永远不触发。
 *
 * 为此由 TabLayout 为每个缓存节点提供本 Context：
 *   value = 该节点是否为当前显示页签（e.key === 当前 cacheKey）。
 *
 * 默认 true：不在缓存容器内（登录页、独立渲染、测试）时按「始终激活」处理，
 * 此时 active 不会发生 false→true 跳变，故不会误触发刷新，行为与改动前一致（仅首挂拉取）。
 *
 * 注意：value 是布尔基元，同一渲染中未切页签时各节点值不变（true 恒 true / false 恒 false），
 * React 按 Object.is 比较不会重渲染任何 consumer，完整保留 TabLayout「隐藏页不陪跑」的不变量；
 * 仅切页签时涉及的两个节点（旧激活 true→false、新激活 false→true）的 consumer 会重渲染。
 */
export const KeepAliveActiveContext = createContext<boolean>(true);

/** 读取当前组件所在缓存页签是否处于激活（显示）态。 */
export const useKeepAliveActive = () => useContext(KeepAliveActiveContext);

/**
 * 读取「上一个激活的缓存页签 key（pathname+search）」的稳定访问器。
 *
 * 用途：列表页在被切回（active false→true）时判断来源——
 *   来自本模块的详情/编辑/卡片页 → 刷新列表（展示刚保存的数据）；
 *   来自其他页签（不同模块）→ 保持原样不刷新，不重新初始化。
 *
 * 之所以用「函数访问器」而非直接给 key 值：key 每次导航都会变，若作为 context value 直接下发，
 * 会导致所有缓存页 consumer 每次导航都重渲染，破坏「隐藏页不陪跑」不变量；
 * 访问器函数身份恒定 → Provider value 不变 → 不触发任何重渲染，仅在需要时惰性读取当前值。
 */
export const KeepAlivePrevActiveKeyContext = createContext<() => string | null>(() => null);

/** 获取读取「上一个激活缓存页 key」的稳定访问器。 */
export const useKeepAlivePrevActiveKey = () => useContext(KeepAlivePrevActiveKeyContext);

/**
 * 读取当前缓存页签激活态的 **ref 版本**。
 *
 * 与 `useKeepAliveActive()` 的区别，也是它存在的唯一理由：返回值身份恒定，
 * 放进 effect 依赖数组不会触发重跑。适用于「定时器回调需要跳过隐藏期间的执行，
 * 但 effect 重跑本身有副作用（重置状态、清空面板、重发请求）」的场景 ——
 * 此时把 active 加进依赖会造成切回页签时数据闪空，只能在回调内用 ref 判定。
 *
 * ref 在渲染期同步赋值，早于任何 effect 读取，故回调取到的恒是本次渲染的激活态。
 * 与 `useKeepAliveActive` 一致：不在缓存容器内时默认 true，不会误跳过执行。
 *
 * @example
 * const activeRef = useKeepAliveActiveRef();
 * useEffect(() => {
 *   const timer = setInterval(() => { if (activeRef.current) void fetchData(); }, 30000);
 *   return () => clearInterval(timer);
 * }, []); // ← 依赖不动，切页签不重跑、不清状态
 */
export function useKeepAliveActiveRef() {
  const active = useContext(KeepAliveActiveContext);
  const ref = useRef(active);
  ref.current = active;
  return ref;
}
